import isNil from 'lodash/isNil';
import omitBy from 'lodash/omitBy';

import { getRegionCountry, getRegionState } from '@packages/stores';
import { AnalyticsCommonProps, PLATFORM } from '@packages/types';
import { getCookie, getQueryString } from '@packages/utils';

import { AnalyticsBrowser } from '@segment/analytics-next';
import { captureException, withScope } from '@sentry/react';
import { useTripStore } from '@store';
import { WebAnalyticsEvent } from '@types';

import { webEnv } from '../envSchema';

const __DEV__ = process.env.NODE_ENV === 'development';
const analytics = AnalyticsBrowser.load({
  writeKey: webEnv.VITE_SEGMENT_WRITE_KEY,
});

/*
    By default Segment will only forward events to Mixpanel
    Enable event forwarding to other destinations by passing a custom options object
    when making a call to sendAnalyticsEvent
     */
const defaultSegmentOptions = {
  integrations: {
    All: true,
    Slack: false,
  },
};

export const sendAnalyticsEvent = <T extends keyof WebAnalyticsEvent['Track']>(
  eventName: T,
  properties?: WebAnalyticsEvent['Track'][T],
  options?: { [key: string]: unknown },
) => {
  withScope(scope => {
    scope.setTag('source', 'sendAnalyticsEvent');
    try {
      const quoteId = useTripStore.getState().trip?.metaData?.quoteId;
      const quotePartnerName = useTripStore.getState().trip?.metaData?.quotePartnerName;

      const commonProps = omitBy(
        {
          platform: PLATFORM.WEB,
          region: getRegionCountry(),
          quoteId,
          quotePartnerName,
        },
        isNil,
      ) as AnalyticsCommonProps;

      analytics
        .track(
          eventName,
          { ...commonProps, ...properties },
          { ...defaultSegmentOptions, ...options },
        )
        .catch(e => {
          throw e;
        });

      if (__DEV__) {
        console.log('Segment Track ========>', eventName);
        console.log('Segment Track Properties ========>', properties);
      }
    } catch (e) {
      captureException(e);
    }
  });
};

export const sendAnalyticsPage = <T extends keyof WebAnalyticsEvent['Page']>(
  eventName: T,
  properties?: WebAnalyticsEvent['Page'][T],
  options?: { [key: string]: unknown },
) => {
  withScope(scope => {
    scope.setTag('source', 'sendAnalyticsPage');
    try {
      const quoteId = useTripStore.getState().trip?.metaData?.quoteId;
      const quotePartnerName = useTripStore.getState().trip?.metaData?.quotePartnerName;

      const commonProps = omitBy(
        {
          platform: PLATFORM.WEB,
          region: getRegionCountry(),
          quoteId,
          quotePartnerName,
        },
        isNil,
      ) as AnalyticsCommonProps;

      analytics
        .page(
          eventName,
          { ...commonProps, ...properties },
          { ...defaultSegmentOptions, ...options },
        )
        .catch(e => {
          throw e;
        });

      if (__DEV__) {
        console.log('Segment Page ========>', eventName);
      }
    } catch (e) {
      captureException(e);
    }
  });
};

export const sendAnalyticsUserId = (
  userId: string | null,
  properties: { [key: string]: unknown } = {},
  options: { [key: string]: unknown } = defaultSegmentOptions,
) => {
  withScope(scope => {
    scope.setTag('source', 'sendAnalyticsUserId');
    try {
      const region = getRegionState();
      const quoteId = useTripStore.getState().trip?.metaData?.quoteId;
      const quotePartnerName = useTripStore.getState().trip?.metaData?.quotePartnerName;

      const commonProps = omitBy(
        {
          platform: PLATFORM.WEB,
          region: getRegionCountry(),
          state: region?.state,
          quoteId,
          quotePartnerName,
          deviceId: localStorage.getItem('deviceId'),
        },
        isNil,
      ) as AnalyticsCommonProps;

      analytics.identify(userId, { ...commonProps, ...properties }, options);

      if (__DEV__) {
        console.log('Segment Identify =========>', userId);
      }
    } catch (e) {
      captureException(e);
    }
  });
};

export const sendAnalyticsECommerce = <T extends keyof WebAnalyticsEvent['Track']>(
  eventName: T,
  properties?: WebAnalyticsEvent['Track'][T],
) => {
  sendAnalyticsEvent(eventName, properties, {
    integrations: {
      All: true,
      Slack: false,
    },
  });
  if (__DEV__) {
    console.log('Segment ECommerce Track ========>', eventName);
  }
};

export const getAnonymousId = async () => {
  return await withScope(async scope => {
    scope.setTag('source', 'getAnonymousId');
    try {
      return (await analytics.user())?.anonymousId();
    } catch (e) {
      captureException(e);
    }
  });
};

export const setMarketingIds = () => {
  const irclickid = getQueryString('irclickid');
  if (irclickid) localStorage.setItem('irclickid', irclickid);

  const gclid = getQueryString('gclid');
  if (gclid) localStorage.setItem('gclid', gclid);

  const gbraid = getQueryString('gbraid');
  if (gbraid) localStorage.setItem('gbraid', gbraid);

  const wbraid = getQueryString('wbraid');
  if (wbraid) localStorage.setItem('wbraid', wbraid);

  const anonymousId = getQueryString('marketinganonymousid') ?? getCookie('ajs_anonymous_id');
  if (anonymousId) localStorage.setItem('marketingAnonymousId', anonymousId);
};

export const getMarketingIds = () =>
  ({
    irclickid: localStorage.getItem('irclickid'),
    anonymousId: localStorage.getItem('marketingAnonymousId'),
    gclid: localStorage.getItem('gclid'),
    gbraid: localStorage.getItem('gbraid'),
    wbraid: localStorage.getItem('wbraid'),
  }) as const;

export const removeMarketingIds = () => {
  localStorage.removeItem('irclickid');
  localStorage.removeItem('marketingAnonymousId');
  localStorage.removeItem('gclid');
  localStorage.removeItem('gbraid');
  localStorage.removeItem('wbraid');
};
